/**
 * Shared formula engine for evaluating column formulas.
 *
 * Provides field flattening (so users can write `status` instead of
 * `issue.fields.status?.name`) and aggregation helpers (SUM, AVG, etc.)
 * that operate over children issues.
 */

/**
 * Recursively collect ALL descendants of an issue from a childrenMap.
 * Returns a flat array of all children, grandchildren, etc. (not including the issue itself).
 *
 * @param {string} issueKey   – the parent issue key
 * @param {object} childrenMap – map of issueKey → direct children array
 * @returns {Array} all descendant issues
 */
// Memoized per (childrenMap object, issueKey). Structure.jsx calls this once
// per CELL during render (rows × columns × subtree walk — the single worst
// per-render cost at folder scale). Sound because every childrenMap update in
// Structure.jsx replaces the per-container map object rather than mutating it
// (the immutable setState convention), so object identity keys freshness; the
// WeakMap lets dropped maps garbage-collect. Callers treat the result as
// read-only (verified: no in-place sort/push on returned arrays).
const _descendantsCache = new WeakMap();
export function collectAllDescendants(issueKey, childrenMap) {
  if (!childrenMap || typeof childrenMap !== 'object') return [];
  let byKey = _descendantsCache.get(childrenMap);
  if (!byKey) { byKey = new Map(); _descendantsCache.set(childrenMap, byKey); }
  const hit = byKey.get(issueKey);
  if (hit) return hit;
  const result = [];
  const seen = new Set();
  const collect = (key) => {
    const children = childrenMap[key] || [];
    for (const child of children) {
      if (seen.has(child.key)) continue;
      seen.add(child.key);
      result.push(child);
      collect(child.key);
    }
  };
  collect(issueKey);
  byKey.set(issueKey, result);
  return result;
}

/**
 * Build a map from normalised field name → Jira field id.
 *
 * Normalisation: lowercase, spaces/underscores/hyphens stripped.
 *   "ZAR Value Excl VAT" → "zarvalueexclvat" → "customfield_10136"
 *
 * Standard fields (status, summary, …) map to themselves.
 * Custom fields are indexed both by normalised name AND by raw id.
 *
 * @param {Array} jiraFields – array returned by the Jira fields endpoint
 * @returns {Map<string, string>}
 */
export function buildFieldNameMap(jiraFields) {
  const map = new Map();

  // Standard fields that live directly on issue.fields
  const standardIds = [
    'summary', 'status', 'assignee', 'reporter', 'priority',
    'issuetype', 'created', 'updated', 'duedate', 'labels',
    'components', 'fixVersions', 'resolution', 'project',
    'timeoriginalestimate', 'timeestimate', 'timespent',
    'description', 'environment',
  ];
  for (const id of standardIds) {
    map.set(id, id);
  }
  // key lives on the issue root, but we still want it accessible
  map.set('key', 'key');

  if (!jiraFields || !jiraFields.length) return map;

  for (const field of jiraFields) {
    const id = field.id;
    // Always allow access by raw id
    map.set(id, id);

    // Also index by normalised human name
    const name = field.name?.trim() || field.untranslatedName?.trim();
    if (name) {
      const normalised = name.toLowerCase().replace(/[^a-z0-9]/g, '');
      // Don't overwrite if the name collides with a standard field id
      if (!map.has(normalised)) {
        map.set(normalised, id);
      }
    }

    // Index by clause names (JQL aliases)
    if (field.clauseNames) {
      for (const clause of field.clauseNames) {
        const norm = clause.toLowerCase().replace(/[^a-z0-9]/g, '');
        if (!map.has(norm)) {
          map.set(norm, id);
        }
      }
    }
  }

  return map;
}

/**
 * Resolve a single Jira field value to a display-friendly primitive.
 * Objects → extract .value / .name / .displayName; arrays → join; numbers stay numbers.
 */
function resolveFieldValue(raw) {
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'number') return raw;
  if (typeof raw === 'string') return raw;
  if (typeof raw === 'boolean') return raw;

  // Arrays (labels, components, fixVersions, etc.)
  if (Array.isArray(raw)) {
    return raw.map(resolveFieldValue);
  }

  // Objects — try common Jira shapes
  if (typeof raw === 'object') {
    if (raw.displayName !== undefined) return raw.displayName;
    if (raw.name !== undefined) return raw.name;
    if (raw.value !== undefined) return raw.value;
    // Fallback to the object itself so formulas can still drill in
    return raw;
  }

  return raw;
}

/**
 * Flatten an issue into a plain object keyed by normalised field name.
 *
 * The result lets formula authors write `status` instead of
 * `issue.fields.status?.name`.
 *
 * @param {object} issue      – raw Jira issue
 * @param {Map}    fieldNameMap – from buildFieldNameMap
 * @returns {object}
 */
export function flattenIssue(issue, fieldNameMap) {
  if (!issue) return {};

  const ctx = {};
  const fields = issue.fields || {};

  // Populate every known mapping
  for (const [alias, fieldId] of fieldNameMap) {
    // `key` lives on the issue root
    if (fieldId === 'key') {
      ctx[alias] = issue.key || '';
      continue;
    }
    const raw = fields[fieldId];
    ctx[alias] = resolveFieldValue(raw);
  }

  // Also set raw customfield_XXXXX references for any fields present on the
  // issue but not yet in the map (edge case: fields returned by JQL that
  // weren't in the /field metadata)
  for (const [fieldId, raw] of Object.entries(fields)) {
    if (!(fieldId in ctx)) {
      ctx[fieldId] = resolveFieldValue(raw);
    }
  }

  // Derived: statuscategory from status.statusCategory.name
  if (!ctx.statuscategory && fields.status?.statusCategory?.name) {
    ctx.statuscategory = fields.status.statusCategory.name;
  }

  return ctx;
}

/**
 * Evaluate a formula string against an issue.
 *
 * @param {object} issue    – raw Jira issue
 * @param {string} formula  – JavaScript expression / statements
 * @param {object} options
 * @param {Array}  options.children     – raw child issues (for aggregation)
 * @param {Array}  options.jiraFields   – Jira field metadata
 * @param {Map}    options.fieldNameMap  – from buildFieldNameMap
 * @returns {string|number} evaluated result or 'Error'
 */
export function evaluateFormula(issue, formula, options = {}) {
  if (!formula) return '';

  const {
    children = [],
    jiraFields = [],
    fieldNameMap = new Map(),
    level = 0,
  } = options;

  let body = '';
  try {
    // Flatten the current issue
    const ctx = flattenIssue(issue, fieldNameMap);

    // Flatten all children for aggregation callbacks
    const flatChildren = children.map(c => flattenIssue(c, fieldNameMap));

    // --- Aggregation helpers ---------------------------------------------------
    const _toValues = (fnOrKey) => {
      if (typeof fnOrKey === 'function') {
        return flatChildren.map(c => {
          try { return fnOrKey(c); } catch { return undefined; }
        });
      }
      if (typeof fnOrKey === 'string') {
        return flatChildren.map(c => c[fnOrKey]);
      }
      // No argument — return undefined array (used by COUNT())
      return flatChildren.map(() => true);
    };

    const _numericValues = (fnOrKey) =>
      _toValues(fnOrKey).map(v => (typeof v === 'string' ? parseFloat(v) : Number(v))).filter(v => !isNaN(v));

    const SUM = (fnOrKey) => {
      const nums = _numericValues(fnOrKey);
      return nums.reduce((a, b) => a + b, 0);
    };

    const AVG = (fnOrKey) => {
      const nums = _numericValues(fnOrKey);
      return nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : 0;
    };

    const MIN = (fnOrKey) => {
      const nums = _numericValues(fnOrKey);
      return nums.length ? Math.min(...nums) : 0;
    };

    const MAX = (fnOrKey) => {
      const nums = _numericValues(fnOrKey);
      return nums.length ? Math.max(...nums) : 0;
    };

    const COUNT = (fnOrKey) => {
      if (fnOrKey === undefined) return flatChildren.length;
      const vals = _toValues(fnOrKey);
      return vals.filter(v => v !== null && v !== undefined && v !== '' && v !== false).length;
    };

    // --- Build the function body -----------------------------------------------
    // Collect variable names declared by the user's formula (const/let/var)
    // so we don't inject conflicting `var` declarations for those names.
    const userDeclaredNames = new Set();
    const declRegex = /\b(?:const|let|var)\s+([a-zA-Z_$][a-zA-Z0-9_$]*)/g;
    let declMatch;
    while ((declMatch = declRegex.exec(formula)) !== null) {
      userDeclaredNames.add(declMatch[1]);
    }

    // Ensure any customfield referenced in the formula exists in ctx (Jira omits empty fields entirely)
    const cfRegex = /\bcustomfield_\d+\b/g;
    let cfMatch;
    while ((cfMatch = cfRegex.exec(formula)) !== null) {
      if (!(cfMatch[0] in ctx)) ctx[cfMatch[0]] = undefined;
    }

    // Inject every flattened field as a local variable declaration so the formula
    // can reference e.g. `status` directly.
    const varDeclarations = Object.entries(ctx)
      .map(([k]) => {
        const safeName = _safeVarName(k);
        if (!safeName) return '';
        if (userDeclaredNames.has(safeName)) return '';
        return `var ${safeName} = __ctx[${JSON.stringify(k)}];`;
      })
      .filter(Boolean)
      .join('\n');

    // Sanitize formula: fix common copy-paste issues (smart quotes, invisible chars)
    body = formula.trim()
      .replace(/\/\/.*$/gm, '')                        // strip single-line comments BEFORE collapsing newlines
      .replace(/[\r\n]/g, ' ')                         // newlines → spaces (prevents broken string literals)
      .replace(/[\u2018\u2019\u201A\uFF07]/g, "'")  // smart single quotes → '
      .replace(/[\u201C\u201D\u201E\uFF02]/g, '"')  // smart double quotes → "
      .replace(/[\u2013\u2014]/g, '-')               // en/em dashes → -
      .replace(/[\u200B\u200C\u200D\uFEFF]/g, '')   // zero-width chars
      .replace(/\u00A0/g, ' ')                          // non-breaking space → space
      .replace(/[\u{10000}-\u{10FFFF}]/gu, ch =>        // astral-plane chars (emojis) → Unicode escapes
        `\\u{${ch.codePointAt(0).toString(16)}}`);

    // Determine wrapping strategy
    const hasReturn = /\breturn\b/.test(body);
    const isMultiStatement = /\b(const|let|var)\b/.test(body) && body.includes(';');

    if (hasReturn) {
      // User wrote explicit return — use as-is
      body = varDeclarations + '\n' + body;
    } else if (isMultiStatement) {
      // Multiple statements without return — auto-return the last expression
      const lastSemiIdx = body.lastIndexOf(';');
      const beforeLast = body.substring(0, lastSemiIdx + 1);
      const lastExpr = body.substring(lastSemiIdx + 1).trim();
      if (lastExpr) {
        body = varDeclarations + '\n' + beforeLast + '\nreturn (' + lastExpr + ');';
      } else {
        // Last statement ends with ; — find the actual last statement
        // Split by ; and return the last non-empty one
        const statements = body.split(';').map(s => s.trim()).filter(Boolean);
        if (statements.length > 1) {
          const allButLast = statements.slice(0, -1).join(';\n') + ';';
          const last = statements[statements.length - 1];
          body = varDeclarations + '\n' + allButLast + '\nreturn (' + last + ');';
        } else {
          body = varDeclarations + '\nreturn (' + body + ');';
        }
      }
    } else {
      // Simple expression
      body = varDeclarations + '\nreturn (' + body + ');';
    }

    // Build and execute
    const fn = new Function(
      'issue', '__ctx', 'children', '_children',
      'SUM', 'AVG', 'MIN', 'MAX', 'COUNT', 'level',
      body,
    );

    const result = fn(
      issue, ctx, flatChildren, children,
      SUM, AVG, MIN, MAX, COUNT, level,
    );

    if (result === undefined || result === null) return '';
    if (typeof result === 'number') return result;
    // Allow formulas to return styled objects: { text, color, fontWeight, ... }
    if (typeof result === 'object' && result !== null && 'text' in result) return result;
    return String(result);
  } catch (err) {
    console.error('Formula evaluation error:', err.message);
    return 'Error';
  }
}

/**
 * Extract Jira field IDs referenced by a formula string.
 *
 * Scans the formula for:
 *   1. Quoted strings — e.g. SUM("Story Points") → resolves via fieldNameMap
 *   2. customfield_XXXXX identifiers — direct field IDs
 *   3. Bare identifiers — e.g. `status`, `duedate` → resolves via fieldNameMap
 *
 * @param {string} formula    – the formula expression
 * @param {Map}    fieldNameMap – from buildFieldNameMap
 * @returns {Set<string>} set of resolved Jira field IDs
 */
export function extractFormulaFieldIds(formula, fieldNameMap) {
  const fieldIds = new Set();
  if (!formula || !fieldNameMap) return fieldIds;

  // 1. Extract quoted strings: "Story Points", 'ZAR Value'
  const quotedRegex = /["']([^"']+)["']/g;
  let match;
  while ((match = quotedRegex.exec(formula)) !== null) {
    const name = match[1];
    const normalised = name.toLowerCase().replace(/[^a-z0-9]/g, '');
    const resolved = fieldNameMap.get(normalised);
    if (resolved) fieldIds.add(resolved);
    // Also try the raw name as a field ID (e.g. customfield_10015)
    if (fieldNameMap.has(name)) fieldIds.add(fieldNameMap.get(name));
  }

  // 2. Extract customfield_XXXXX identifiers
  const customFieldRegex = /\bcustomfield_\d+\b/g;
  while ((match = customFieldRegex.exec(formula)) !== null) {
    fieldIds.add(match[0]);
  }

  // 3. Extract bare identifiers and resolve via fieldNameMap
  const identRegex = /\b([a-zA-Z_][a-zA-Z0-9_]*)\b/g;
  while ((match = identRegex.exec(formula)) !== null) {
    const ident = match[1];
    // Skip JS keywords and formula built-ins
    if (_FORMULA_SKIP.has(ident)) continue;
    const resolved = fieldNameMap.get(ident);
    if (resolved) fieldIds.add(resolved);
    // Also try normalised
    const normalised = ident.toLowerCase().replace(/[^a-z0-9]/g, '');
    const resolvedNorm = fieldNameMap.get(normalised);
    if (resolvedNorm) fieldIds.add(resolvedNorm);
  }

  return fieldIds;
}

const _FORMULA_SKIP = new Set([
  'SUM', 'AVG', 'MIN', 'MAX', 'COUNT',
  'issue', 'children', 'Math', 'Number', 'String', 'parseInt', 'parseFloat',
  'true', 'false', 'null', 'undefined', 'NaN', 'Infinity',
  'if', 'else', 'return', 'var', 'let', 'const', 'function',
  'for', 'while', 'do', 'break', 'continue', 'switch', 'case', 'default',
  'typeof', 'instanceof', 'new', 'this', 'void', 'delete', 'in',
  'try', 'catch', 'finally', 'throw', 'class', 'extends', 'super',
  'import', 'export', 'async', 'await', 'yield',
  'map', 'filter', 'reduce', 'forEach', 'find', 'some', 'every',
  'length', 'push', 'pop', 'join', 'split', 'trim', 'slice',
  'toFixed', 'toString', 'toLowerCase', 'toUpperCase',
  'abs', 'ceil', 'floor', 'round', 'max', 'min', 'pow', 'sqrt',
  'log', 'replace', 'includes', 'startsWith', 'endsWith',
  'text', 'color', 'fontWeight', 'backgroundColor', 'value', 'name',
]);

// Names that must not be used as var declarations (they would shadow
// the function parameters or injected helpers).
const _RESERVED_NAMES = new Set([
  'issue', '__ctx', 'children', '_children',
  'SUM', 'AVG', 'MIN', 'MAX', 'COUNT', 'level',
  // JS reserved words
  'break', 'case', 'catch', 'continue', 'debugger', 'default', 'delete',
  'do', 'else', 'finally', 'for', 'function', 'if', 'in', 'instanceof',
  'new', 'return', 'switch', 'throw', 'try', 'typeof', 'var',
  'void', 'while', 'with', 'class', 'const', 'enum', 'export', 'extends',
  'import', 'super', 'implements', 'interface', 'let', 'package', 'private',
  'protected', 'public', 'static', 'yield', 'async', 'await',
  // JS built-ins that can't be var names
  'undefined', 'null', 'true', 'false',
  'this', 'arguments', 'eval', 'NaN', 'Infinity',
]);

/**
 * Ensure a string is a valid JS identifier for use in `var` declarations.
 * Returns the string unchanged if valid, otherwise returns a mangled version.
 * Returns empty string (which gets filtered out) if completely unusable.
 */
function _safeVarName(name) {
  // Must start with letter/$/_ and contain only word chars
  if (!/^[a-zA-Z_$][a-zA-Z0-9_$]*$/.test(name)) return '';
  // Don't shadow function parameters or helpers
  if (_RESERVED_NAMES.has(name)) return '';
  return name;
}
